import { AppMainStateContext } from '../contexts/AppMainStateContext';
import { DeviceSize } from '../consts/device.ts';
import { AppState, AppStateService, VideoQualityLevelPreference } from '../services/appStateService.ts';
import { Orientation } from '../consts/orientation.ts';
import { NotificationStatus } from '../components/notification/Notification.tsx';
import { ReactNode, useEffect, useState } from 'react';

interface IProps {
    children: ReactNode;
}

export const AppMainStateProvider = ({ children }: IProps) => {
    const _appStateService = new AppStateService();
    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    const [orientation, setOrientation] = useState<Orientation>(
        _appStateService.getOrientation(),
    );
    const [appStates, setAppStates] = useState<AppState>(
        _appStateService.get(),
    );
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isDisabledCloseModal, setIsDisabledCloseModal] = useState(false);
    const [isNotificationVisible, setIsNotificationVisible] = useState(false);
    const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
    const [isSearchModalOpen, setIsSearchModalOpen] = useState(false);
    const [notificationData, setNotificationData] = useState({
        description: '',
        status: NotificationStatus.info,
        title: '',
    });
    const [windowWidth, setWindowWidth] = useState<DeviceSize>(
        _appStateService.getDeviceSize(),
    );
    const closeModal = () => {
        const body = document.querySelector('body') as HTMLElement;
        body.classList.remove('overflow-hidden');
        setIsModalOpen(false);
        setIsLoginModalOpen(false);
        setIsDisabledCloseModal(false);
    };
    const openModal = () => {
        const body = document.querySelector('body') as HTMLElement;
        body.classList.add('overflow-hidden');
        setIsModalOpen(true);
    };

    const openLoginModal = (closeModalDisabled: boolean = false) => {
        const body = document.querySelector('body') as HTMLElement;
        body.classList.add('overflow-hidden');
        setIsLoginModalOpen(true);
        setIsModalOpen(true);
        setIsDisabledCloseModal(closeModalDisabled);
    };

    useEffect(() => {
        const result = _appStateService.get();
        setIsCookiesAgreed(result.isCookiesAgreed);
        setVideoQualityPreference(result.qualityLevelPreference);
        window.addEventListener('resize', handleWindowSizeChange);
        window.addEventListener('orientationchange', handleOrientationChange);
        return () => {
            window.removeEventListener('resize', handleWindowSizeChange);
            window.removeEventListener('orientationchange', handleOrientationChange);
        };
    }, []);

    const handleWindowSizeChange = () => {
        setWindowWidth(_appStateService.getDeviceSize());
        setIsMobile(window.innerWidth <= DeviceSize.tabletL);
    };
    const handleOrientationChange = () => {
        setOrientation(_appStateService.getOrientation());
    };
    const setIsCookiesAgreed = (isAgreed: boolean | 'not set') => {
        _appStateService.setIsCookiesAgreed(isAgreed);
        setAppStates(_appStateService.get());
    };
    const setVideoQualityPreference = (videoQualityLevel: VideoQualityLevelPreference) => {
        _appStateService.setQualityLevelPreference(videoQualityLevel);
        setAppStates(_appStateService.get());
    };

    return (
        <AppMainStateContext.Provider
            value={{
                isMobile,
                isIOS: /iPad|iPhone|iPod/.test(navigator.userAgent),
                appStates,
                isModalOpen,
                isSearchModalOpen,
                isDisabledCloseModal,
                isLoginModalOpen,
                isNotificationVisible,
                notificationData,
                orientation,
                windowWidth,
                setIsCookiesAgreed,
                setVideoQualityPreference,
                setIsSearchModalOpen,
                setIsNotificationVisible,
                setNotificationData,
                closeModal,
                openModal,
                openLoginModal,
            }}
        >
            {children}
        </AppMainStateContext.Provider>
    );
};
